/** @file One-bone IK: aim, compress/stretch, uniform scale, mixed toward FK. */
import { angle } from '@pose2d/math/angle';
import { trig } from '@pose2d/math/trig';
import { POSE_LAYOUT } from '../contracts/pose';
import type { Pose } from '../contracts/pose';
import { effectiveParent } from '../pose/effectiveParent';
import { updateBoneWorld } from '../pose/updateBoneWorld';
import type { InheritMode, SkeletonData } from '../skeletonData';
import { aimLocalRotation } from './aimLocalRotation';
import { readWorld } from './readWorld';
import { solverChain } from './solverChain';

const L = POSE_LAYOUT;
/** Offsets inside a world affine [a, b, c, d, tx, ty] (math/affine): X axis (a, c), origin (tx, ty). */
const X_AXIS_A = 0;
const X_AXIS_C = 2;
const TX = 4;
const TY = 5;
/** Below this distance (px) the target sits on the joint, below this |det E| the parent is singular: FK is kept. */
const EPS = 1e-9;
const PARENT = new Float64Array(L.WORLD_STRIDE);
const E = new Float64Array(L.WORLD_STRIDE);

/** Scale factor along the bone: 1 unless compress (dist < len) or stretch (dist > len) applies; mixed by mix. */
function stretchFactor(pose: Pose, k: number, len: number, dist: number, mix: number): number {
  const compress = (pose.ik[k + L.IK_COMPRESS] ?? 0) >= 0.5 && dist < len;
  const stretch = (pose.ik[k + L.IK_STRETCH] ?? 0) >= 0.5 && dist > len;
  return len > EPS && (compress || stretch) ? 1 + (dist / len - 1) * mix : 1;
}

/**
 * Writes the constrained bone applied rotation/scale for constraint ik and recomputes its world. Reads the FK
 * input from pose.applied (updatePose resets applied from local each frame) and the target's world from
 * pose.world (the update order computes it first). mix 0, a target on the joint, a non-finite target or a singular
 * effective parent leaves the FK world. A constraint that is not a one-bone chain throws IK_INVALID (solverChain).
 */
export function solveOneBone(pose: Pose, data: SkeletonData, ik: number): void {
  const c = solverChain(data, ik, 1);
  const bone = c.bones[0] ?? 0;
  updateBoneWorld(pose, data, bone);
  const k = ik * L.IK_STRIDE;
  const mix = pose.ik[k + L.IK_MIX] ?? 0;
  const w = bone * L.WORLD_STRIDE;
  const t = c.target * L.WORLD_STRIDE;
  const jx = pose.world[w + TX] ?? 0;
  const jy = pose.world[w + TY] ?? 0;
  const tx = pose.world[t + TX] ?? 0;
  const ty = pose.world[t + TY] ?? 0;
  const dist = trig.hypot(tx - jx, ty - jy);
  // A target on the joint, or a non-finite one (NaN or infinite world), gives no direction: FK is kept.
  if (mix === 0 || !(dist >= EPS && dist < Number.POSITIVE_INFINITY)) return;
  const a = pose.applied;
  const o = bone * L.LOCAL_STRIDE;
  readWorld(pose, data.bones[bone]?.parent ?? -1, PARENT);
  effectiveParent(E, PARENT, (pose.inherit[bone] ?? 0) as InheritMode);
  const detE = (E[0] ?? 0) * (E[3] ?? 0) - (E[1] ?? 0) * (E[2] ?? 0);
  if (!(Math.abs(detE) > EPS)) return; // singular E (zero-scale parent): the aim is undefined, keep FK (R6)
  // A negative scaleX points the bone backwards: turn it 180 degrees so its tip, not its tail, faces the target.
  const flip = (a[o + L.SCALE_X] ?? 0) < 0 ? 180 : 0;
  const fk = a[o + L.ROTATION] ?? 0;
  const aimed = aimLocalRotation(E, jx, jy, tx, ty, a[o + L.SHEAR_X] ?? 0) + flip;
  a[o + L.ROTATION] = fk + angle.wrap180(aimed - fk) * mix;
  updateBoneWorld(pose, data, bone);
  const len = (data.bones[bone]?.length ?? 0) * trig.hypot(pose.world[w + X_AXIS_A] ?? 0, pose.world[w + X_AXIS_C] ?? 0);
  const f = stretchFactor(pose, k, len, dist, mix);
  if (f === 1) return;
  a[o + L.SCALE_X] = (a[o + L.SCALE_X] ?? 0) * f;
  if (c.uniform) a[o + L.SCALE_Y] = (a[o + L.SCALE_Y] ?? 0) * f;
  updateBoneWorld(pose, data, bone);
}
